import { useMemo } from "react";
import intl from "react-intl-universal";
import {
  TbBuildingBank,
  TbCircleCheck,
  TbClock,
  TbCreditCard,
  TbCurrencyDollar,
} from "react-icons/tb";
import { SummaryCard } from "../../../../../Components/Atoms/SummaryCard";
import { formatMonetaryValue } from "../../../../../Utils/formatters";

const formatCount = (value) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(value);

/**
 * Client-side totals from the loaded GET /v1/cards list.
 * Issuer accounts have no dedicated aggregate endpoint; program-level
 * credit_summary / card_totals stay on Emissor (programs / home).
 */
export const computeBaasCardAccountTotals = (accounts = []) => {
  let pending = 0;
  let active = 0;
  let linked = 0;
  let creditLimitSum = 0;
  let creditCurrency = "BRL";

  accounts.forEach((account) => {
    const status = String(account?.status || "").toUpperCase();
    if (status === "PENDING") pending += 1;
    else if (status === "ACTIVE") active += 1;
    if (account?.accountId) linked += 1;

    const limit = account?.creditInfo?.creditLimit;
    if (limit) {
      creditLimitSum += Number(limit.amount) || 0;
      if (limit.currency) creditCurrency = limit.currency;
    }
  });

  return {
    total: accounts.length,
    pending,
    active,
    linked,
    unlinked: Math.max(0, accounts.length - linked),
    creditLimitSum,
    creditCurrency,
  };
};

export const EMPTY_BAAS_CARD_ACCOUNT_TOTALS = computeBaasCardAccountTotals([]);

export const BaasCardAccountsKpiCards = ({ accounts = [] }) => {
  const totals = useMemo(() => computeBaasCardAccountTotals(accounts), [accounts]);

  const metrics = useMemo(
    () => [
      {
        key: "total",
        icon: TbCreditCard,
        title: intl.get("baas.cartoes.accounts.totals.total"),
        value: formatCount(totals.total),
        subtext: intl.get("baas.cartoes.accounts.totals.totalSubtext"),
      },
      {
        key: "pending",
        icon: TbClock,
        title: intl.get("baas.cartoes.accounts.totals.pending"),
        value: formatCount(totals.pending),
        subtext: intl.get("baas.cartoes.accounts.totals.pendingSubtext"),
      },
      {
        key: "active",
        icon: TbCircleCheck,
        title: intl.get("baas.cartoes.accounts.totals.active"),
        value: formatCount(totals.active),
        subtext: intl.get("baas.cartoes.accounts.totals.activeSubtext"),
      },
      {
        key: "creditLimit",
        icon: TbCurrencyDollar,
        title: intl.get("baas.cartoes.accounts.totals.creditLimit"),
        value: formatMonetaryValue(totals.creditLimitSum, totals.creditCurrency, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }),
        subtext: intl.get("baas.cartoes.accounts.totals.creditLimitSubtext"),
      },
      {
        key: "linked",
        icon: TbBuildingBank,
        title: intl.get("baas.cartoes.accounts.totals.linked"),
        value: formatCount(totals.linked),
        subtext: intl.get("baas.cartoes.accounts.totals.linkedSubtext", {
          count: totals.unlinked,
        }),
      },
    ],
    [totals],
  );

  return (
    <section className="min-w-0 w-full">
      <p className="dashboard-section-label mb-3">
        {intl.get("baas.cartoes.accounts.totalsSection")}
      </p>
      <div className="dashboard-card-grid">
        {metrics.map((metric) => (
          <SummaryCard
            key={metric.key}
            icon={metric.icon}
            title={metric.title}
            value={metric.value}
            footerLabel={metric.subtext}
          />
        ))}
      </div>
    </section>
  );
};
